import { useMutation, useQuery } from "@tanstack/react-query";
import { sessionApiService, ScanSampleCollectionApiService, ScanSampleSeperationApiService } from "modules/Session/Session/api/session.api";

interface ApiResponse<T = any> {
  data: T;
  message: string;
  statusCode: number;
}

export const useScanningSessionService = (payload?: any) => {
  const InsertMutation = useMutation<ApiResponse, Error, any>({
    mutationFn: (payload: any) => sessionApiService.insertData(payload),
  });

  const ValidateMutation = useMutation<ApiResponse, Error, any>({
    mutationFn: (payload: any) => sessionApiService.validateData(payload),
  });

  // Commented out as AccessioningSession/Select is no longer used
  // const SelectQuery = useQuery<ApiResponse, Error>({
  //   queryKey: ["scanning-session-select", payload?.sessionid],
  //   queryFn: () => sessionApiService.selectData(payload),
  //   enabled: payload?.isSelectEnabled !== undefined ? payload.isSelectEnabled : false,
  // });

  // Commented out as AccessioningSession/SelectAll is no longer used
  // const SelectAllQuery = useQuery<ApiResponse, Error>({
  //   queryKey: ["scanning-session-select-all", payload?.searchtext, payload?.currentpage, payload?.pagesize, payload?.studyid],
  //   queryFn: () => sessionApiService.selectAllData(payload),
  //   enabled: payload?.enabled !== undefined ? payload.enabled : !!payload?.studyid,
  // });

  const UpdateMutation = useMutation<ApiResponse, Error, any>({
    mutationFn: (payload: any) => sessionApiService.updateData(payload),
  });

  return { InsertMutation, ValidateMutation, UpdateMutation };
};

// export const useScanningSessionProgressService = (payload?: any) => {
//   const SessionProgressQuery = useQuery<ApiResponse, Error>({
//     queryKey: ["scanning-session-progress", payload?.sessionid, payload?.searchtext, payload?.currentpage, payload?.pagesize, payload?.tabstatus],
//     queryFn: () => sessionApiService.scanProgressData(payload),
//     enabled: !!payload?.sessionid,
//   });

//   return { SessionProgressQuery };
// };

export const useSampleCollectionScanProgressService = (payload?: any) => {
  const SampleCollectionScanProgressQuery = useQuery<ApiResponse, Error>({
    queryKey: [
      "sample-collection-scan-progress",
      payload?.sessionid,
      payload?.searchtext,
      payload?.tabstatus,
      payload?.currentpage,
      payload?.pagesize,
    ],
    queryFn: () => sessionApiService.sampleCollectionScanProgressData(payload),
    enabled: payload?.enabled !== undefined ? payload.enabled : !!payload?.sessionid,
  });

  return { SampleCollectionScanProgressQuery };
};

export const useSampleSeperationScanProgressService = (payload?: any) => {
  const SampleSeperationScanProgressQuery = useQuery<ApiResponse, Error>({
    queryKey: [
      "sample-seperation-scan-progress",
      payload?.sessionid,
      payload?.searchtext,
      payload?.tabstatus,
      payload?.currentpage,
      payload?.pagesize,
    ],
    queryFn: () => sessionApiService.sampleSeperationScanProgressData(payload),
    enabled: payload?.enabled !== undefined ? payload.enabled : !!payload?.sessionid,
  });

  return { SampleSeperationScanProgressQuery };
};

export const useScanningScanSampleCollectionService = (payload?: any) => {
  const InsertMutation = useMutation<ApiResponse, Error, any>({
    mutationFn: (payload: any) => ScanSampleCollectionApiService.insertData(payload),
  });

  const ValidateSubjectMutation = useMutation<ApiResponse, Error, any>({
    mutationFn: (payload: any) => ScanSampleCollectionApiService.validateSubjectBarcode(payload),
  });

  const SelectAllQuery = useQuery<ApiResponse, Error>({
    queryKey: ["scanning-scan-sample-collection-select-all", payload?.sessionid, payload?.searchtext, payload?.currentpage, payload?.pagesize],
    queryFn: () => ScanSampleCollectionApiService.selectAllData(payload),
    enabled: payload?.isSelectAllEnabled !== undefined ? payload.isSelectAllEnabled : (payload?.enabled !== undefined ? payload.enabled : !!payload?.sessionid),
  });

  const SampleCollectionSessionSelectQuery = useQuery<ApiResponse, Error>({
    queryKey: ["sample-collection-session-select", payload?.sessionid],
    queryFn: () => ScanSampleCollectionApiService.sampleCollectionSessionSelectData(payload),
    enabled: payload?.isSessionSelectEnabled !== undefined ? payload.isSessionSelectEnabled : (payload?.enabled !== undefined ? payload.enabled : !!payload?.sessionid),
  });

  return { InsertMutation, ValidateSubjectMutation, SelectAllQuery, SampleCollectionSessionSelectQuery };
};

export const useScanSampleCollectionSummarySelectAllService = (
  payload?: any
) => {
  const SelectAllQuery = useQuery<ApiResponse, Error>({
    queryKey: [
      "scan-sample-collection-summary-select-all",
      payload?.studyid,
      payload?.periodid,
      Array.isArray(payload?.timepointid) ? payload.timepointid.join(",") : payload?.timepointid,
      payload?.dosingstationno,
      payload?.searchtext,
      payload?.currentpage,
      payload?.pagesize,
    ],
    queryFn: () => ScanSampleCollectionApiService.collectionSummarySelectAllData(payload),
    enabled: payload?.enabled !== undefined ? payload.enabled : !!payload?.studyid,
  });

  return { SelectAllQuery };
};

export const useScanSampleCollectionTotalAttemptsHistoryService = (
  payload?: any
) => {
  const SelectAllQuery = useQuery<ApiResponse, Error>({
    queryKey: [
      "scan-sample-collection-total-attempts-history",
      payload?.sessionid,
      payload?.subjectbarcode,
      payload?.currentpage,
      payload?.pagesize,
    ],
    queryFn: () => ScanSampleCollectionApiService.totalAttemptsHistoryData(payload),
    enabled: !!payload?.sessionid && !!payload?.subjectbarcode,
    staleTime: 0,
    refetchOnMount: "always",
  });

  return { SelectAllQuery };
};

export const useScanningScanSampleSeperationService = (payload?: any) => {
  const InsertMutation = useMutation<ApiResponse, Error, any>({
    mutationFn: (payload: any) => ScanSampleSeperationApiService.insertData(payload),
  });

  const ValidateVaccutainerMutation = useMutation<ApiResponse, Error, any>({
    mutationFn: (payload: any) => ScanSampleSeperationApiService.validateVaccutainerBarcode(payload),
  });

  const SelectAllQuery = useQuery<ApiResponse, Error>({
    queryKey: ["scanning-scan-sample-seperation-select-all", payload?.sessionid, payload?.searchtext, payload?.currentpage, payload?.pagesize],
    queryFn: () => ScanSampleSeperationApiService.selectAllData(payload),
    enabled: payload?.isSelectAllEnabled !== undefined ? payload.isSelectAllEnabled : (payload?.enabled !== undefined ? payload.enabled : !!payload?.sessionid),
  });

  const SampleSeperationSessionSelectQuery = useQuery<ApiResponse, Error>({
    queryKey: ["sample-seperation-session-select", payload?.sessionid],
    queryFn: () => ScanSampleSeperationApiService.sampleSeperationSessionSelectData(payload),
    enabled: payload?.isSessionSelectEnabled !== undefined ? payload.isSessionSelectEnabled : (payload?.enabled !== undefined ? payload.enabled : !!payload?.sessionid),
  });

  return { InsertMutation, ValidateVaccutainerMutation, SelectAllQuery, SampleSeperationSessionSelectQuery };
};

export const useScanSampleSeperationSummarySelectAllService = (
  payload?: any
) => {
  const SelectAllQuery = useQuery<ApiResponse, Error>({
    queryKey: [
      "scan-sample-seperation-summary-select-all",
      payload?.studyid,
      payload?.periodid,
      Array.isArray(payload?.timepointid) ? payload.timepointid.join(",") : payload?.timepointid,
      payload?.aliquotid,
      payload?.dosingstationno,
      payload?.searchtext,
      payload?.currentpage,
      payload?.pagesize,
    ],
    queryFn: () => ScanSampleSeperationApiService.seperationSummarySelectAllData(payload),
    enabled: payload?.enabled !== undefined ? payload.enabled : !!payload?.studyid
  });

  return { SelectAllQuery };
};

export const useScanSampleSeperationTotalAttemptsHistoryService = (
  payload?: any
) => {
  const SelectAllQuery = useQuery<ApiResponse, Error>({
    queryKey: [
      "scan-sample-seperation-total-attempts-history",
      payload?.sessionid,
      payload?.vaccutainerbarcode,
      payload?.currentpage,
      payload?.pagesize,
    ],
    queryFn: () => ScanSampleSeperationApiService.totalAttemptsHistoryData(payload),
    enabled: !!payload?.sessionid && !!payload?.vaccutainerbarcode,
    staleTime: 0,
    refetchOnMount: "always",
  });

  return { SelectAllQuery };
};

export const useAccessioningSessionScanlogProgressHistoryService = (
  payload?: any
) => {
  const SelectAllQuery = useQuery<ApiResponse, Error>({
    queryKey: [
      "accessioning-session-scanlog-progress-history",
      payload?.studyid,
      payload?.periodid,
      Array.isArray(payload?.timepointid) ? payload.timepointid.join(",") : payload?.timepointid,
      payload?.dosingstationno,
      payload?.tabstatus,
      payload?.searchtext,
      payload?.currentpage,
      payload?.pagesize,
    ],
    queryFn: () => sessionApiService.scanlogProgressHistoryData(payload),
    enabled: payload?.enabled !== undefined ? payload.enabled : !!payload?.studyid,
  });

  return { SelectAllQuery };
};
